import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useTranslation } from "../i18n/LanguageContext";
import { resolveList } from "../config/eventOptions";
import { getEventFallbackImage } from "../utils/eventImages";
import FallbackImage from "./FallbackImage";
import { formatEventDate } from "../utils/formatDate";

// Compact event preview rendered inside a chat bubble for `type: "event"`
// messages. Only the eventId is ever stored in the message — this always
// live-fetches the event through the normal events/{eventId} security rule,
// so it automatically reflects the event's current state and never renders
// stale or unauthorized data (e.g. if the sender's/recipient's access
// changed, or the event was edited/removed, after the message was sent).
function EventMessageCard({ eventId }) {
  const { t, language } = useTranslation();
  const [state, setState] = useState("loading"); // loading | found | unavailable
  const [event, setEvent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    setEvent(null);

    (async () => {
      try {
        const snap = await getDoc(doc(db, "events", eventId));
        if (cancelled) return;
        if (!snap.exists()) {
          setState("unavailable");
          return;
        }
        setEvent({ id: snap.id, ...snap.data() });
        setState("found");
      } catch {
        if (!cancelled) setState("unavailable");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [eventId]);

  if (state === "loading") {
    return (
      <div className="w-56 max-w-full rounded-xl border border-slate-600/60 bg-slate-800/80 p-3 text-xs text-slate-400">
        {t("chat.eventLoading", "Loading event…")}
      </div>
    );
  }

  if (state === "unavailable" || !event) {
    return (
      <div className="w-56 max-w-full rounded-xl border border-slate-600/60 bg-slate-800/80 p-3 text-xs text-slate-400">
        {t("chat.eventUnavailable", "This event is no longer available.")}
      </div>
    );
  }

  const genres = resolveList(event.genres, event.genre);
  const moods = resolveList(event.moods, event.mood);
  const fallbackImage = getEventFallbackImage({ id: event.id, genres, moods });

  return (
    <Link
      to={`/event/${event.id}`}
      className="block w-56 max-w-full overflow-hidden rounded-xl border border-slate-600/60 bg-slate-800/90 shadow-md transition hover:border-sky-500/60"
    >
      <FallbackImage
        src={event.imageUrl}
        fallbackSrc={fallbackImage}
        alt=""
        className="h-24 w-full object-cover"
        placeholderClassName="flex h-24 w-full items-center justify-center bg-slate-700 text-slate-500"
        placeholderLabel={t("event.noImage", "No image")}
      />
      <div className="p-2.5">
        <p className="truncate text-sm font-semibold text-white">{event.name}</p>
        <p className="mt-0.5 truncate text-[11px] text-slate-400">
          {[event.date && formatEventDate(event.date, language), event.venue || event.city]
            .filter(Boolean)
            .join(" · ")}
        </p>
        <p className="mt-1 text-[11px] font-semibold text-sky-300">
          {t("chat.viewEvent", "View Event")} →
        </p>
      </div>
    </Link>
  );
}

export default EventMessageCard;
